import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Layout from "src/components/program/layout/Layout";
import SEO from "src/components/common/SEO";
import Stepper from "src/components/common/Stepper";
import MobileNumber from "src/components/onboarding/MobileNumber";
import OTP from "src/components/onboarding/OTP";
import UserType from "src/components/onboarding/UserType";
import PatientName from "src/components/onboarding/PatientName";
import PatientDOB from "src/components/onboarding/PatientDOB";
import PatientGender from "src/components/onboarding/PatientGender";
import DoctorDetails from "src/components/onboarding/DoctorDetails";
import UploadDocuments from "src/components/onboarding/UploadDocuments";

import environment from "src/constants/environment";
import supportPrograms from "src/data/app/programs";
import analytics from "src/helpers/analytics";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { updateProfileRequest } from "src/redux/slices/authSlice";

const OnboardingScreen: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const config =
    supportPrograms[params.support_program_key as keyof typeof supportPrograms];

  const [currentStep, setCurrentStep] = useState(0);
  const [onboardingSteps, setOnboardingSteps] = useState<any[]>([]);
  const [data, setData] = useState<Record<string, any>>({});

  const dispatch = useAppDispatch();
  const { updateProfile } = useAppSelector((state) => state.auth);

  // add once in your app root
  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        document.body.classList.add("is-typing");
      }
    };
    const onFocusOut = (e: FocusEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        document.body.classList.remove("is-typing");
      }
    };

    window.addEventListener("focusin", onFocusIn);
    window.addEventListener("focusout", onFocusOut);

    return () => {
      window.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  useEffect(() => {
    // Event: ONBOARDING_SCREEN_VIEWED
    analytics.pageView(SCREEN_NAMES.ONBOARDING_SCREEN);
  }, []);

  useEffect(() => {
    if (config?.onboarding?.steps) {
      setOnboardingSteps(config.onboarding.steps);
    }
  }, [config]);

  useEffect(() => {
    // if (updateProfile.data) {
    // console.log("updateProfile", updateProfile);
    // }
  }, [updateProfile]);

  const updateProfileFunc = (data: any) => {
    const doctorDetails = data.doctor_details_capture;
    const payload = {
      name: data.patient_name_capture,
      gender: data.patient_gender_selection,
      dob: data.patient_dob_capture
        ? new Date(data.patient_dob_capture).toISOString()
        : "",
      access_code: doctorDetails?.accessCode ?? "",
      state: doctorDetails?.state ?? "",
      city: doctorDetails?.city ?? "",
      medical_condition_group_id:
        environment?.ENV === "prod"
          ? config?.medical_condition_group_id?.prod
          : config?.medical_condition_group_id?.uat,
      patient_meta_info: {
        user_type: data.user_type_selection,
        hospital: doctorDetails?.hospital ?? "",
        doctor_name: doctorDetails?.doctorName ?? "",
        support_program_type: config?.support_program_name,
        support_program_key: config?.support_program_key,
      },
    };

    new Promise((resolve, reject) => {
      dispatch(
        updateProfileRequest({
          payload: {
            payload,
            header: {
              userFrom: "PSP",
              source: "GoodFlipCare",
            },
          },
          resolve,
          reject,
        }),
      );
    })
      .then((res: any) => {
        if (Number(res?.code || 0) === 1) {
          // Event: ONBOARDING_SUCCESS
          analytics.trackEvent(EVENT_NAMES.ONBOARDING_SUCCESS, {
            screen_name: SCREEN_NAMES.ONBOARDING_SCREEN,
            support_program_key: params.support_program_key,
          });

          // Set User Attributes
          analytics.setUserAttributes(res?.data);

          // Navigate to success screen
          navigate(
            `/programs/${params.support_program_key}/onboarding/success`,
          );
        }
      })
      .catch((error) => {
        console.error("Error while onboarding", error);
        // Event: ONBOARDING_ERROR
        analytics.trackEvent(EVENT_NAMES.ONBOARDING_ERROR, {
          screen_name: SCREEN_NAMES.ONBOARDING_SCREEN,
          support_program_key: params.support_program_key,
          error: error?.message || "Something went wrong!",
        });
      });
  };

  const onNext = (key: string, value: any) => {
    const newData = { ...data, [key]: value };
    setData(newData);
    if (currentStep === onboardingSteps.length - 1) {
      // update profile
      updateProfileFunc(newData);
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const onBack = () => {
    const step = currentStep > 1 ? currentStep - 1 : 0;
    setCurrentStep(step);
  };

  const currentStepData = onboardingSteps[currentStep];
  if (!currentStepData) return null;

  // console.log("currentStepData", currentStepData);

  const commonProps = {
    title: currentStepData.title,
    description: currentStepData.description,
    onBack,
    onNext: (value?: any) => onNext(currentStepData.id, value),
    value: data[currentStepData.id],
    mobileNumber: data.mobile_number_capture, // Pass if needed
    medical_condition_group_id:
      environment?.ENV === "prod"
        ? config?.medical_condition_group_id?.prod
        : config?.medical_condition_group_id?.uat,
    support_program_type: config?.support_program_name,
    support_program_key: config?.support_program_key,
  };

  const renderStep = (type: string) => {
    switch (type) {
      case "mobile_number_capture":
        return (
          <MobileNumber
            {...commonProps}
            onSuccess={(value: string) => commonProps.onNext(value)}
          />
        );
      case "otp_capture":
        return <OTP {...commonProps} />;
      case "user_type_selection":
        return <UserType {...commonProps} />;
      case "patient_name_capture":
        return <PatientName {...commonProps} />;
      case "patient_gender_selection":
        return <PatientGender {...commonProps} />;
      case "patient_dob_capture":
        return <PatientDOB {...commonProps} />;
      case "doctor_details_capture":
        return <DoctorDetails {...commonProps} />;
      case "upload_documents":
        return (
          <UploadDocuments
            {...commonProps}
            documentType={config?.documents?.document_type}
            documents={config?.documents?.required_documents || []}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Layout
      enableTheme={true}
      supportProgramKey={params.support_program_key}
      // className="bg-white"
    >
      <SEO
        title={`Onboarding - ${config?.psp_name}`}
        description={`Onboarding for ${config?.psp_name}`}
      />
      <div className="flex flex-col flex-1 min-h-0">
        <div className="shrink-0">
          <Stepper
            totalSteps={onboardingSteps.length}
            currentStep={currentStep + 1}
            className="px-4 pt-6 pb-4 border-b border-gray-200 bg-white"
          />
        </div>

        <div className="flex-1 min-h-0 flex flex-col">
          {renderStep(currentStepData.id)}
        </div>
      </div>
    </Layout>
  );
};

export default OnboardingScreen;
